import { TextBlock } from '../interfaces/textEditor.interface';
import { imageDeleteMessage } from './imageDeleteMessage';
import {
  handleRemoveContentBlock,
  resolveBlockImage,
} from './removeContentBlock';

interface RemoveBlockArgs {
  block: TextBlock;
  username: string;
  draftId: string;
  // biome-ignore lint/suspicious/noExplicitAny: Formik arrayHelpers
  arrayHelpers: any;
  index: number;
  /** Called with the surviving image key removed, so the caller can prune its own key list. */
  onKeyRemoved: (key: string) => void;
  /** Called with user-facing copy when something went wrong. */
  onError: (message: string) => void;
}

/**
 * The block-removal handler shared by the post and reply composers.
 *
 * EXTRACTED SO THE WIRING IS TESTABLE (TRIL-615 review). The copy selection was covered by
 * imageDeleteMessage's own tests, but nothing asserted that the component actually CALLS it —
 * deleting `setImageDeleteError(...)` from both composers left the whole suite green. Testing that
 * through the real components would mean mounting Formik + Redux + Apollo to assert one string; a
 * callback boundary gets the same coverage honestly.
 *
 * It also removes the second near-duplicate in this pair. The `handleRemoveContentBlock` twins were
 * byte-identical with tests on only one side, which is how the reply composer could silently lose
 * the fix; these two handlers had the same shape and the same risk.
 */
export const removeBlockWithFeedback = async ({
  block,
  username,
  draftId,
  arrayHelpers,
  index,
  onKeyRemoved,
  onError,
}: RemoveBlockArgs): Promise<void> => {
  // Resolved ONCE, here, and handed down — so "was there an image?" and "could we delete it?"
  // cannot disagree between the two functions. They previously did, and the user got the wrong copy.
  const resolved = resolveBlockImage(block);
  // Tracked rather than inferred: the catch cannot otherwise tell "Formik threw before removal"
  // from "the delete failed after it", and those need opposite copy.
  let blockRemoved = false;
  let deleted = false;

  try {
    await handleRemoveContentBlock(
      block,
      username,
      draftId,
      {
        remove: (i: number) => {
          arrayHelpers.remove(i);
          blockRemoved = true;
        },
      },
      index,
      resolved,
    );
    deleted = true;
  } catch (error) {
    console.error('Failed to remove the content block:', error);
    // `hasImage`, NOT `key !== null`: an Image block whose url would not parse still HAD an image,
    // and the user needs to hear about it. Keying the copy off the resolved key told them
    // "something went wrong removing that block, please try again" — about a block already removed.
    onError(
      imageDeleteMessage(error, {
        hasImage: resolved.hasImage,
        blockRemoved,
      }),
    );
    return;
  }

  // Outside the try on purpose: this is a Redux dispatch, and if it throws that is not a storage
  // failure. Inside, it would have been reported to the user as "the image could not be deleted"
  // even though the delete had already succeeded.
  if (deleted && resolved.key) onKeyRemoved(resolved.key);
};
